import { z } from 'zod'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { useContext, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'

import { AnalyticsEvent, DistanceMetrics, RoutesPath } from 'src/types'
import { useLocales } from 'src/contexts'
import { KEYS_FOR_INVALIDATION } from 'src/constants'

import { useNavigation } from 'src/hooks/useNavigation'
import { useManageUserCars } from 'src/hooks/useManageUserCars'
import { useCarModels } from 'src/hooks/useAutoInfo'
import { useAppSelector } from 'src/hooks/useAppSelector'
import { omitObjectValues } from 'src/helpers/omitObjectValues'
import { AnalyticsContext } from 'src/contexts/AnalyticsContext'

import { fuelTypeDictionary } from 'src/pages/AutoInfoForm/helpers'
import { autoInfoFormSchema } from 'src/pages/AutoInfoForm/constants'
import { queryClient } from 'src/features/AppProviders/constants'

export const useAutoInfoForm = () => {
  const { translations } = useLocales()
  const { navigateTo } = useNavigation()
  const { logEvent } = useContext(AnalyticsContext)

  const { distanceMetric } = useAppSelector(state => state.user)

  const [brand, setBrand] = useState('Ford')

  const { modelsForMake, isLoadingGetModelsForMake } = useCarModels(brand)
  const { addCar, isLoadingAddCar } = useManageUserCars()

  const autoInfoForm = useForm({
    resolver: zodResolver(autoInfoFormSchema),
    defaultValues: {
      carBrand: '',
      carModel: '',
      year: '',
      mileage: '',
      vin: '',
      distanceMetric: DistanceMetrics.KM,
      engineType: 'Petrol',
      licensePlate: '',
      lastServiceDate: new Date(),
      nextMaintenance: new Date(),
      isMainCar: true,
    },
  })

  const handleOnError = (error: unknown) => {
    toast(translations.trackFuel.historyFuel.toast.error.title, {
      description: `${error}`,
      action: {
        label: 'Okay',
        onClick: () => toast.dismiss(),
      },
    })
  }

  function onSubmit(data: z.infer<typeof autoInfoFormSchema>) {
    const newData = omitObjectValues(data, ['isMainCar', 'licensePlate'])
    addCar(
      {
        isActive: data.isMainCar ?? true,
        carNumber: data.licensePlate,
        distanceMetric: data.distanceMetric,
        ...newData,
        mileage: Number(data.mileage) || 0,
      },
      {
        onSuccess: () => {
          autoInfoForm.reset()
          logEvent(AnalyticsEvent.ADDING_NEW_CAR)
          toast(translations.userData.toaster.success.title, {
            description: translations.userData.toaster.success.description,
            action: {
              label: 'Okay',
              onClick: () => toast.dismiss(),
            },
          })

          KEYS_FOR_INVALIDATION.forEach(key => {
            queryClient.invalidateQueries({ queryKey: [key] })
          })
          navigateTo(RoutesPath.MY_CARS)
        },
        onError: handleOnError,
      },
    )
  }

  const getFormattedTitle = (name: string, title: string): string => {
    const isMileage = name === 'mileage'

    if (isMileage) return `${title} (${distanceMetric})`

    return title
  }

  return {
    locale: translations.addCar,
    autoInfoForm,
    setBrand,
    isLoadingGetModelsForMake,
    onSubmit,
    modelsForMake,
    isLoadingAddCar,
    fuelTypeDictionary: fuelTypeDictionary(translations.addCar),
    getFormattedTitle,
  }
}
